import type { AgenticLogicRuleRunCapture } from "@intelligems/ig-types/functions/agentic-logic/runs/capture";
import type { AgenticLogicRuleRunDto } from "@intelligems/ig-types/dtos/agentic-logic/rule-runs";

/**
 * Deep links into Datadog Logs for one recorded rule run.
 *
 * Every query here is built from what the run itself carries, so the links
 * work for a row read back from Snowflake as well as a live one. The ids are
 * JSON attributes on the log lines — `@invocationId` is stamped by the rule
 * runtime's marker logger and by the SDK-read broker, `@orgId` /
 * `@visitorId` / `@placementId` ride the backend's structured log records —
 * and the function names are the deterministic per-stage names the tiers
 * invoke. `functionname` is how the Datadog Lambda extension tags a
 * function's log lines; the tag value is lowercased on ingest.
 */
export interface DatadogRunLogLink {
  key: string;
  label: string;
  /** What the query asks, in one sentence; shown as the button's tooltip. */
  description: string;
  /** The Datadog Logs search, verbatim, for copying into a wider search. */
  query: string;
  url: string;
}

export const DATADOG_LOGS_URL = "https://app.datadoghq.com/logs";

const MINUTE_MS = 60 * 1000;

/** Slack around the evaluation for the logs of the run itself. */
const RUN_WINDOW_BEFORE_MS = 1 * MINUTE_MS;
const RUN_WINDOW_AFTER_MS = 2 * MINUTE_MS;
/** Wider slack for the shopper- and placement-scoped searches. */
const CONTEXT_WINDOW_MS = 15 * MINUTE_MS;

export function datadogLogsUrl(options: {
  query: string;
  fromMs: number;
  toMs: number;
}): string {
  const params = new URLSearchParams({
    query: options.query,
    from_ts: String(options.fromMs),
    to_ts: String(options.toMs),
    live: "false",
  });
  return `${DATADOG_LOGS_URL}?${params.toString()}`;
}

/** A Datadog attribute filter with the value quoted, so ids with `-` match whole. */
const attribute = (name: string, value: string): string =>
  `@${name}:"${value.replaceAll('"', '\\"')}"`;

/**
 * Datadog tags a function's logs with its bare name, lowercased on ingest;
 * an invoked `name:ALIAS` qualifier is not part of the tag.
 */
const functionTag = (functionName: string): string =>
  `functionname:${functionName.split(":")[0]!.toLowerCase()}`;

/**
 * The template runner shares a backend pool Lambda with other handlers, so
 * its lines are the pool's lines that carry its logger context
 * (`run-template-runner.lambda.handler.ts`).
 */
const TEMPLATE_RUNNER_CONTEXT = "@context:AgenticLogicTemplateRunner";

/**
 * The function that ran the rule. A template rule runs in one of the
 * stage's backend pools, narrowed to the runner's lines: the captured pool
 * when the run was recorded live, otherwise any pool of the stage. A custom
 * rule runs in its own function; its name is per org and rule and is not
 * derivable from the row, so the fallback there is the tier's service tag.
 */
function ruleComputeQuery(
  run: AgenticLogicRuleRunDto,
  stage: string,
  capture: AgenticLogicRuleRunCapture | undefined
): string {
  if ((capture?.input.compute.tier ?? run.executionTier) === "custom") {
    return capture
      ? functionTag(capture.input.compute.functionName)
      : `service:agentic-logic-rule ${attribute("invocationId", run.invocationId)}`;
  }
  const pool = capture
    ? functionTag(capture.input.compute.functionName)
    : `functionname:${stage}-be-pool-*`;
  return `${pool} ${TEMPLATE_RUNNER_CONTEXT}`;
}

export function agenticLogicRunDatadogLinks(
  run: AgenticLogicRuleRunDto,
  options: { stage: string; capture?: AgenticLogicRuleRunCapture }
): DatadogRunLogLink[] {
  const { stage, capture } = options;
  const startMs = run.evaluatedAtMs;
  const endMs = run.evaluatedAtMs + run.durationMs;
  const runWindow = {
    fromMs: startMs - RUN_WINDOW_BEFORE_MS,
    toMs: endMs + RUN_WINDOW_AFTER_MS,
  };
  const contextWindow = {
    fromMs: startMs - CONTEXT_WINDOW_MS,
    toMs: endMs + CONTEXT_WINDOW_MS,
  };
  const link = (
    key: string,
    label: string,
    description: string,
    query: string,
    window: { fromMs: number; toMs: number }
  ): DatadogRunLogLink => ({
    key,
    label,
    description,
    query,
    url: datadogLogsUrl({ query, ...window }),
  });

  return [
    link(
      "invocation",
      "Invocation",
      "Every log line stamped with this invocation id: the rule's own output and any SDK read it made.",
      attribute("invocationId", run.invocationId),
      runWindow
    ),
    link(
      "rule",
      "Rule Lambda",
      "The Lambda that executed the rule, around the time it ran.",
      ruleComputeQuery(run, stage, capture),
      runWindow
    ),
    link(
      "sdk-reads",
      "SDK reads",
      "The trusted read function's lines for this org while the rule ran.",
      `${functionTag(`${stage}-AgenticLogicSdkRead`)} ${attribute("orgId", run.orgId)}`,
      runWindow
    ),
    link(
      "broker",
      "Serving broker",
      "The backend's Agentic Logic lines for this org around the evaluation: assignment resolution, fallback, telemetry publication.",
      `${attribute("orgId", run.orgId)} @context:AgenticLogic*`,
      runWindow
    ),
    link(
      "visitor",
      "Visitor",
      "Everything logged for this shopper in the fifteen minutes either side of the run.",
      attribute("visitorId", run.visitorId),
      contextWindow
    ),
    link(
      "placement",
      "Placement",
      "Every evaluation of this placement in the fifteen minutes either side of the run.",
      attribute("placementId", run.placementId),
      contextWindow
    ),
  ];
}
